import { FC, useEffect } from 'react';
import {
  Navigate,
  Outlet,
  createBrowserRouter,
  RouterProvider
} from 'react-router';

import { disableProductRegistration, isConstruction } from 'config';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { authSelector, checkSession } from 'store/authState';

import LoginPage from 'pages/LoginPage';
import MyProfilePage from 'pages/ProfilePage';
import SignUpPage from 'pages/SignUpPage';
import ProductsPage from 'pages/ProductsPage';
import ProductDetailsPage from 'pages/ProductsPage/ProductDetailsPage';
import ProductRegistrationPage from 'pages/ProductRegistrationPage';
import RegisterProductsPage from 'pages/RegisterProductsPage';
import RegisterProfessionalProductsPage from 'pages/RegisterProfessionalProductsPage';
import ProductInformationPage from 'pages/ProductInformationPage';
import ResetPasswordPage from 'pages/ResetPasswordPage';
import VerifyEmailPage from 'pages/SignUpPage/VerifyEmailPage';
import ConfirmChangeEmailPage from 'pages/ConfirmChangeEmailPage';
import ConfirmDeleteAccountPage from 'pages/ConfirmDeleteAccountPage';
import EmailVerificationPage from 'pages/SignUpPage/VerifyEmailPage/EmailVerificationPage';
import EditProfilePage from 'pages/ProfilePage/EditProfilePage';
import NotFoundPage from 'pages/NotFoundPage';
import { CompleteProfilePage } from 'pages/SignUpPage/CompleteProfilePage';
import { CreateAccountPage } from 'pages/SignUpPage/CreateAccountPage';
import { CompanyDetailsPage } from 'pages/SignUpPage/CompanyDetailsPage';
import { CompanyMainContactPage } from 'pages/SignUpPage/CompanyMainContactPage';
import { TermsOfUsePage } from 'pages/TermsOfUsePage';
import { ServicesPage } from 'pages/ServicesPage';

import ErrorBoundary from 'components/fragments/ErrorBoundary';
import Layout from 'components/fragments/Layout';
import PageWrapper from 'components/UI/Page/PageWrapper';
import Loader from 'components/UI/Loader';

import * as routes from './routes';

// Protected route
const PrivateRoute: FC = () => {
  const {
    authCheck,
    session: { authenticated, user }
  } = useAppSelector(authSelector);

  if (!authCheck) {
    return (
      <PageWrapper>
        <Loader center size="large" />
      </PageWrapper>
    );
  }

  if (!authenticated) {
    return <Navigate to={routes.LOGIN} />;
  }

  // If user is authenticated but does not have a customer account, redirect to signup
  if (!user?.attributes.customerId) {
    return (
      <Navigate to={isConstruction ? routes.SIGNUP_COMPANY : routes.SIGNUP} />
    );
  }

  return <Outlet />;
};

// Root route
const RootRoute: FC = () => {
  const {
    session: { authenticated }
  } = useAppSelector(authSelector);

  if (!authenticated) {
    return <Navigate to={routes.LOGIN} />;
  }

  return <Navigate to={routes.PRODUCTS} />;
};

// Router
const router = createBrowserRouter([
  {
    Component: ErrorBoundary,
    children: [
      {
        Component: Layout,
        children: [
          {
            Component: PrivateRoute,
            children: [
              ...(!disableProductRegistration
                ? [
                    {
                      path: routes.INVITE_PRODUCTS_REG,
                      Component: RegisterProductsPage
                    }
                  ]
                : []),
              {
                path: routes.PRODUCTS,
                children: [
                  {
                    index: true,
                    Component: ProductsPage
                  },
                  {
                    path: ':id',
                    Component: ProductDetailsPage
                  }
                ]
              },
              {
                path: routes.SERVICES,
                Component: ServicesPage
              },
              {
                path: routes.PROFILE,
                children: [
                  {
                    index: true,
                    Component: MyProfilePage
                  },
                  {
                    path: 'edit',
                    Component: EditProfilePage
                  }
                ]
              }
            ]
          },
          {
            path: routes.SIGNUP,
            Component: SignUpPage,
            children: [
              {
                index: true,
                Component: CreateAccountPage
              },
              // Construction only allows registering business accounts
              ...(!isConstruction
                ? [
                    {
                      path: 'complete-profile',
                      Component: CompleteProfilePage
                    }
                  ]
                : []),
              {
                path: 'company',
                Component: CompanyDetailsPage
              },
              {
                path: 'company/main-contact',
                Component: CompanyMainContactPage
              },
              {
                path: 'verify-email',
                Component: VerifyEmailPage
              }
            ]
          },
          {
            path: routes.CONFIRM_CHANGE_EMAIL,
            Component: ConfirmChangeEmailPage
          },
          {
            path: routes.CONFIRM_DELETE_ACCOUNT,
            Component: ConfirmDeleteAccountPage
          },
          {
            path: routes.EMAIL_VERIFICATION,
            Component: EmailVerificationPage
          },
          {
            path: routes.RESET_PASSWORD,
            Component: ResetPasswordPage
          },
          ...(!disableProductRegistration
            ? [
                {
                  path: routes.PRODUCT_REG,
                  Component: ProductRegistrationPage
                },
                {
                  path: routes.PRODUCT_INFO,
                  Component: ProductInformationPage
                },
                {
                  path: routes.PRODUCT_REG_PROFESSIONAL,
                  Component: RegisterProfessionalProductsPage
                }
              ]
            : []),
          {
            path: routes.TERMS_OF_USE,
            Component: TermsOfUsePage
          },
          {
            path: routes.LOGIN,
            Component: LoginPage
          },
          {
            path: routes.ROOT,
            Component: RootRoute
          },
          {
            path: '*',
            Component: NotFoundPage
          }
        ]
      }
    ]
  }
]);

const Router: FC = () => {
  const dispatch = useAppDispatch();

  // Check auth
  useEffect(() => {
    dispatch(checkSession());
  }, [dispatch]);

  return <RouterProvider router={router} />;
};

export default Router;
